import { useRef } from "react";
import { alpha } from "@mui/material/styles";
import { Check } from "lucide-react";
import { RavenIcon } from "@ravenappdev/raven-ui";
import { Box, TextField, Typography } from "../../ds";
import { matchingBand, type Criterion } from "./criteria";

// Column rhythm for the sheet on md+: criterion, guidance, marks. Shared
// by the header row and every criterion row so the three stay aligned.
const COLUMNS = "minmax(150px, 1.1fr) minmax(0, 2.4fr) 96px";

interface BandTilesProps {
  criterion: Criterion;
  value: number | "";
  readOnly: boolean;
  onPick: (v: number) => void;
}

/**
 * The guidance bands as a radio group — the tick-boxes of the paper form.
 * Tiles carry the hint alone; the mark a band is worth is applied by
 * choosing it and read back in the Marks column, so the sheet never shows
 * two numbers for one criterion.
 */
function BandTiles({ criterion, value, readOnly, onPick }: BandTilesProps) {
  const selected = matchingBand(value, criterion.bands);
  const tiles = useRef<Array<HTMLDivElement | null>>([]);

  const focusAndPick = (index: number) => {
    const band = criterion.bands[index];
    if (!band) return;
    onPick(band.v);
    tiles.current[index]?.focus();
  };

  const onKeyDown = (e: React.KeyboardEvent, index: number) => {
    const last = criterion.bands.length - 1;
    if (e.key === "ArrowRight" || e.key === "ArrowDown") {
      e.preventDefault();
      focusAndPick(index === last ? 0 : index + 1);
    } else if (e.key === "ArrowLeft" || e.key === "ArrowUp") {
      e.preventDefault();
      focusAndPick(index === 0 ? last : index - 1);
    } else if (e.key === "Home") {
      e.preventDefault();
      focusAndPick(0);
    } else if (e.key === "End") {
      e.preventDefault();
      focusAndPick(last);
    } else if (e.key === " " || e.key === "Enter") {
      e.preventDefault();
      onPick(criterion.bands[index].v);
    }
  };

  return (
    <Box
      role="radiogroup"
      aria-label={`${criterion.label} guidance`}
      aria-readonly={readOnly || undefined}
      sx={{
        display: "grid",
        gridTemplateColumns: {
          xs: "repeat(auto-fit, minmax(92px, 1fr))",
          md: `repeat(${criterion.bands.length}, minmax(0, 1fr))`,
        },
        gap: 0.75,
      }}
    >
      {criterion.bands.map((band, index) => {
        const active = selected === band.v;
        // Roving tabindex: the chosen tile owns the tab stop, or the
        // first one while nothing is chosen.
        const tabbable = active || (selected === null && index === 0);
        return (
          <Box
            key={band.v}
            ref={(el: HTMLDivElement | null) => {
              tiles.current[index] = el;
            }}
            role="radio"
            aria-checked={active}
            aria-label={band.hint ?? String(band.v)}
            aria-disabled={readOnly || undefined}
            tabIndex={readOnly ? -1 : tabbable ? 0 : -1}
            onClick={readOnly ? undefined : () => onPick(band.v)}
            onKeyDown={readOnly ? undefined : (e) => onKeyDown(e, index)}
            sx={(theme) => ({
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: 0.5,
              minHeight: 34,
              px: 1,
              py: 0.5,
              textAlign: "center",
              borderRadius: "8px",
              border: "1px solid",
              borderColor: active ? "primary.main" : "divider",
              bgcolor: active
                ? alpha(theme.palette.primary.main, 0.1)
                : "transparent",
              color: active ? "primary.dark" : "text.secondary",
              fontSize: 12,
              fontWeight: active ? 600 : 400,
              cursor: readOnly ? "default" : "pointer",
              transition: "background-color .12s ease, border-color .12s ease",
              ...(!readOnly && {
                "&:hover": { borderColor: "primary.main" },
              }),
              "&:focus-visible": {
                outline: "2px solid",
                outlineColor: "primary.main",
                outlineOffset: 2,
              },
            })}
          >
            {active && (
              <RavenIcon
                icon={Check}
                strokeWidth={2}
                sx={{ fontSize: 13 }}
                aria-hidden="true"
              />
            )}
            <Box component="span">{band.hint ?? band.v}</Box>
          </Box>
        );
      })}
    </Box>
  );
}

interface Props {
  criteria: readonly Criterion[];
  values: Record<string, number | "">;
  /** Audit record: tiles show what was chosen, marks read as text. */
  readOnly?: boolean;
  /** Member mode: the committee marks are averaged, so the inputs stay
   *  visible but can't be typed into. */
  locked?: boolean;
  onChange?: (key: string, raw: string) => void;
  onPick?: (key: string, value: number) => void;
}

/**
 * The Annexure scoring sheet: one ruled row per criterion, guidance in the
 * middle, marks on the right. Ruled rows rather than cards — a marking
 * sheet is read down a column, and boxed rows break that read.
 */
export default function CriteriaSheet({
  criteria,
  values,
  readOnly = false,
  locked = false,
  onChange,
  onPick,
}: Props) {
  return (
    <Box>
      <Box
        aria-hidden="true"
        sx={{
          display: { xs: "none", md: "grid" },
          gridTemplateColumns: COLUMNS,
          gap: 2,
          pb: 1,
          borderBottom: "1px solid",
          borderColor: "divider",
        }}
      >
        {/* The design system's table-header treatment (DataTable's header
            cells): one type step for all three columns. */}
        <Typography variant="tableHeader" color="text.primary">
          Criterion
        </Typography>
        <Typography variant="tableHeader" color="text.primary">
          Guidance
        </Typography>
        <Typography
          variant="tableHeader"
          color="text.primary"
          sx={{ textAlign: "right" }}
        >
          Marks
        </Typography>
      </Box>

      {criteria.map((c, idx) => {
        const value = values[c.key] ?? "";
        return (
          <Box
            key={c.key}
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "minmax(0, 1fr)", md: COLUMNS },
              alignItems: { md: "center" },
              gap: { xs: 1, md: 2 },
              py: 1.5,
              ...(idx < criteria.length - 1 && {
                borderBottom: "1px solid",
                borderColor: "divider",
              }),
            }}
          >
            <Box sx={{ minWidth: 0 }}>
              <Typography sx={{ fontSize: 14, fontWeight: 600 }}>
                {c.label}
              </Typography>
              <Typography sx={{ fontSize: 12, color: "text.secondary" }}>
                max {c.max}
              </Typography>
            </Box>

            <BandTiles
              criterion={c}
              value={value}
              readOnly={readOnly}
              onPick={(v) => onPick?.(c.key, v)}
            />

            {readOnly ? (
              <Typography
                sx={{
                  fontSize: 15,
                  fontWeight: 600,
                  textAlign: { xs: "left", md: "right" },
                  fontVariantNumeric: "tabular-nums",
                }}
              >
                {typeof value === "number" ? value : 0}
              </Typography>
            ) : (
              <TextField
                type="number"
                value={value}
                disabled={locked}
                onChange={(e) => onChange?.(c.key, e.target.value)}
                slotProps={{
                  htmlInput: {
                    min: 0,
                    max: c.max,
                    step: 1,
                    "aria-label": `${c.label} score`,
                    style: {
                      textAlign: "right",
                      fontVariantNumeric: "tabular-nums",
                    },
                  },
                }}
                sx={{ width: { xs: 110, md: "100%" } }}
              />
            )}
          </Box>
        );
      })}
    </Box>
  );
}
